import React from "react";
import { HelmetProvider, Helmet } from "react-helmet-async";
import type { HelmetServerState } from "react-helmet-async";

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;           // path under your domain (e.g. /og-images/foo.webp)
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;      // path under your domain
  canonicalUrl?: string;      // like "/servicii/e-commerce"
  structuredData?: object;
  noindex?: boolean;
  nofollow?: boolean;
}

const PageMeta = ({
  title,
  description,
  keywords,
  ogTitle,
  ogDescription,
  ogImage = "/og-images/pixado-default.webp",
  twitterCard = "summary_large_image",
  twitterTitle,
  twitterDescription,
  twitterImage,
  canonicalUrl,
  structuredData,
  noindex = false,
  nofollow = false,
}: SEOProps) => {
  const site = "https://pixado.ro";
  const currentUrl = canonicalUrl ? `${site}${canonicalUrl}` : site;
  const finalOgTitle = ogTitle || title;
  const finalOgDescription = ogDescription || description;
  const finalTwitterTitle = twitterTitle || finalOgTitle;
  const finalTwitterDescription = twitterDescription || finalOgDescription;
  const finalTwitterImage = twitterImage || ogImage;

  return (
    <Helmet>
      {/* Basic Meta */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}

      {/* Canonical */}
      {canonicalUrl && <link rel="canonical" href={currentUrl} />}

      {/* Robots */}
      <meta
        name="robots"
        content={`${noindex ? "noindex" : "index"},${nofollow ? "nofollow" : "follow"},max-snippet:-1,max-image-preview:large,max-video-preview:-1`}
      />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={finalOgTitle} />
      <meta property="og:description" content={finalOgDescription} />
      <meta property="og:image" content={`${site}${ogImage}`} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:site_name" content="Pixado - Agenție Web Premium Timișoara" />
      <meta property="og:locale" content="ro_RO" />

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={finalTwitterTitle} />
      <meta name="twitter:description" content={finalTwitterDescription} />
      <meta name="twitter:image" content={`${site}${finalTwitterImage}`} />
      <meta name="twitter:site" content="@pixado_ro" />
      <meta name="twitter:creator" content="@pixado_ro" />

      {/* Extra */}
      <meta name="author" content="Pixado - Agenție Web Timișoara" />
      <meta name="copyright" content="© 2025 Pixado. Toate drepturile rezervate." />
      <meta name="language" content="Romanian" />
      <meta name="geo.region" content="RO-TM" />
      <meta name="geo.placename" content="Timișoara" />
      <meta name="geo.position" content="45.7489;21.2087" />
      <meta name="ICBM" content="45.7489, 21.2087" />

      {/* JSON-LD */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

// ✅ Fixed AppWrapper so nonce is only read in the browser
type HelmetContext = {
  helmet?: HelmetServerState;
};

type AppWrapperProps = {
  children: React.ReactNode;
  helmetContext?: HelmetContext;
  nonce?: string;
};

export const AppWrapper = ({ children, helmetContext, nonce }: AppWrapperProps) => {
  const [nonceState, setNonceState] = React.useState<string | undefined>(nonce);

  React.useEffect(() => {
    if (nonce) {
      return;
    }
    const m = document.querySelector('meta[name="csp-nonce"]') as HTMLMetaElement | null;
    setNonceState(m?.content || undefined);
  }, [nonce]);

  return (
    <HelmetProvider
      context={helmetContext}
      {...(nonceState ? { nonce: nonceState } : {})}
    >
      {children}
    </HelmetProvider>
  );
};

export default PageMeta;
